import React, { useEffect } from 'react';
import { COLORS } from '@constants';
import { ORDER_ECOMMERCE_STATUS_LABELS, ORDER_FINANCE_STATUS_LABELS, ORDER_FULFILLMENT_STATUS_LABELS } from '@enums';
import { useStore } from '@hooks';
import { Tag, Typography } from '@ui';
import { dayjs, formatString } from '@utils';
import { Flex, Spin } from 'antd';
import { observer } from 'mobx-react';
import { getTimezone } from '../../../../utils/getTimezone';
import OrderDetailsFooter from '../OrderList/components/OrderDetailsFooter';
import { StyledRow } from '../OrderList/components/OrderDetailsFooter/styled';
import { getColumnsInner } from '../OrderList/constants';
import { StyledInnerTable } from '../OrderList/styled';
import { StyledStateWrapper, StyledSummarySection } from './styled';

const DATE_FORMAT = 'D MMM YY';
const EMPTY_VALUE = '-';

const SummaryLine = ({ label, value }) => (
    <StyledRow $borderColor={COLORS.$gray15Black} gap={20} justify='space-between'>
        <Typography.Text data-e2e={`orders_modal-${formatString(label)}_row`} color={COLORS.$gray60Black} type='body2'>
            {label}
        </Typography.Text>
        <Typography.Text data-e2e={`orders_modal-${formatString(label)}_value`} type='badgeSmall'>
            {value}
        </Typography.Text>
    </StyledRow>
);

export const SubscriptionOrderModal = observer(() => {
    const store = useStore();
    const { orderDetails, orderDetailsPending, resetOrderDetails } = store.ordersStore;
    const { isManual, currentAccount } = store.accountsStore;

    useEffect(() => resetOrderDetails, [resetOrderDetails]);

    if (orderDetailsPending) {
        return (
            <StyledStateWrapper align='center' justify='center'>
                <Spin data-e2e='orders_subscription-order-modal_loader' />
            </StyledStateWrapper>
        );
    }

    if (!orderDetails) {
        return (
            <StyledStateWrapper align='center' justify='center'>
                <Typography.Text data-e2e='orders_subscription-order-modal_empty' color={COLORS.$gray60Black}>
                    This order is no longer available.
                </Typography.Text>
            </StyledStateWrapper>
        );
    }

    const formatDate = (value) =>
        value
            ? dayjs(value)
                  .utcOffset(getTimezone({ currentAccount, prefix: null }))
                  .format(DATE_FORMAT)
            : EMPTY_VALUE;

    const summaryLines = [
        { label: 'Klickly ID', value: orderDetails.klicklyOrderNumber || EMPTY_VALUE },
        {
            label: 'Ecommerce ID',
            value: orderDetails.ecommerceOrderName || orderDetails.ecommerceOrderId || EMPTY_VALUE
        },
        {
            label: 'Campaigns',
            value: orderDetails.campaigns.map(({ title }) => title).join(' | ') || EMPTY_VALUE
        },
        { label: 'Created At', value: formatDate(orderDetails.ecommerceCreatedAt) },
        { label: 'Processed At', value: formatDate(orderDetails.processedAt) }
    ];

    const summary = (
        <StyledSummarySection data-e2e='orders_subscription-order-modal_summary' vertical>
            {summaryLines.map(({ label, value }) => (
                <SummaryLine key={label} label={label} value={value} />
            ))}
        </StyledSummarySection>
    );

    const orderItems = orderDetails.orderItems.map((item, key) => ({ ...item, key }));

    return (
        <StyledInnerTable
            data-e2e='orders_subscription-order-modal_content'
            dataSource={orderItems}
            columns={getColumnsInner(isManual)}
            pagination={false}
            borderColor='transparent'
            footer={() => <OrderDetailsFooter record={orderDetails} summary={summary} />}
        />
    );
});

export const SubscriptionOrderModalTitle = observer(() => {
    const store = useStore();
    const { orderDetails } = store.ordersStore;
    const { isBigCommerce } = store.accountsStore;

    if (!orderDetails) return null;

    const statuses = isBigCommerce
        ? [ORDER_ECOMMERCE_STATUS_LABELS[orderDetails.ecommerceStatus]]
        : [
              ORDER_FINANCE_STATUS_LABELS[orderDetails.financialStatus],
              ORDER_FULFILLMENT_STATUS_LABELS[orderDetails.fulfillmentStatus]
          ];

    return (
        <Flex align='center' gap={16}>
            <span data-e2e='orders_subscription-order-modal_title'>{orderDetails.klicklyOrderNumber}</span>
            {statuses.filter(Boolean).map(({ title, color }) => (
                <Tag key={title} color={color ?? COLORS.$gray40Black}>
                    {title}
                </Tag>
            ))}
        </Flex>
    );
});
